/**
 * Каналы и юрлица.
 *
 * Вопросы: какой канал (поставка, розница…) растёт и какой прибыльнее;
 * сколько приносит каждая организация группы; какая часть выручки —
 * продажи своим же юрлицам, то есть задвоение в консолидированной цифре.
 *
 * Источник — «Маржинальность по филиалам», а без неё «Выручка по
 * направлениям» (тогда только выручка, без маржи).
 */

import { useFinance } from '../../finance/context'
import { indexBy, num, rowKey, useReport, useSettled } from '../../finance/data'
import { BarRows, ColumnChart, SegmentBar } from '../../finance/charts'
import { Caveat, DataNotes, Delta, Panel, SkeletonColumns, SkeletonRows } from '../../finance/parts'
import { usePublishFreshness } from '../../finance/freshness'
import { useSales, useSalesSource } from '../../finance/queries'
import { compareLabel, labelOf, monthLabel, monthsOf, shortMonth } from '../../finance/period'
import { missingOf } from '../../finance/coverage'
import { salesEmpty } from '../../finance/frame'
import { SALES_SERIES, money } from '../../finance/sections/Overview'
import { deltaPercent, formatPercent } from '../../format'
import { ValueLegend } from '../charts'
import { labelOr, ranked } from '../parts'

const CHANNEL_COLORS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)']

export function Channels() {
  const live = useFinance()
  const { scoped, catalog } = live
  const source = useSalesSource()
  const withMargin = source?.withMargin ?? false

  const channelsLive = useSales('channel', live.range)
  const channelsBeforeLive = useSales('channel', live.previousRange)
  const orgsLive = useSales('organization', live.range)
  const orgsBeforeLive = useSales('organization', live.previousRange)
  const kindsLive = useSales('counterparty_kind', live.range)
  const code = source?.code ?? 'margin_by_department'
  const channelTrendLive = useReport(
    code,
    source && catalog.hasDimension(source.code, 'channel') ? scoped(source.code, { period: live.trend, group_by: ['period', 'channel'], metrics: ['revenue'] }) : null,
  )
  usePublishFreshness([channelsLive, orgsLive])

  // Текущий и прошлый период — одним набором с подписями своего выбора.
  const settled = useSettled([channelsLive, channelsBeforeLive, orgsLive, orgsBeforeLive, kindsLive, channelTrendLive], live)
  const [channels, channelsBefore, orgs, orgsBefore, kinds, channelTrend] = settled.states
  const ctx = settled.view
  const { range, trend, selection, scopeLabel, coverage, noCompare } = ctx
  const incomplete = Boolean(noCompare)
  const vs = compareLabel(selection)

  const channelRows = ranked(channels.data?.rows)
  const channelTotal = channelRows.reduce((sum, row) => sum + num(row.revenue), 0)
  const orgRows = ranked(orgs.data?.rows)
  const orgTotal = orgRows.reduce((sum, row) => sum + num(row.revenue), 0)
  const kindRows = ranked(kinds.data?.rows)
  const kindTotal = kindRows.reduce((sum, row) => sum + num(row.revenue), 0)
  const internal = kindRows.filter((row) => String(row.counterparty_kind) === 'Своя организация').reduce((sum, row) => sum + num(row.revenue), 0)

  // Динамика: до трёх крупнейших каналов отдельными рядами, остальные — «Прочие».
  const months = monthsOf(trend)
  const rangeMonths = new Set(monthsOf(range))
  const trendTotals = new Map<string, number>()
  for (const row of channelTrend.data?.rows ?? []) {
    const key = labelOr(row.channel, 'Канал не указан')
    trendTotals.set(key, (trendTotals.get(key) ?? 0) + num(row.revenue))
  }
  const order = [...trendTotals.entries()].sort((a, b) => b[1] - a[1]).map(([key]) => key)
  const named = order.length <= 3 ? order : order.slice(0, 2)
  const seriesKeys = order.length <= 3 ? named : [...named, 'Прочие']
  const trendSeries = seriesKeys.map((key, index) => ({ key, label: key, color: CHANNEL_COLORS[index]! }))
  const byMonth = new Map<string, number[]>()
  for (const row of channelTrend.data?.rows ?? []) {
    const month = String(row.period).slice(0, 10)
    const label = labelOr(row.channel, 'Канал не указан')
    const index = named.includes(label) ? named.indexOf(label) : seriesKeys.length - 1
    const values = byMonth.get(month) ?? seriesKeys.map(() => 0)
    values[index] = (values[index] ?? 0) + num(row.revenue)
    byMonth.set(month, values)
  }

  const breakdown = (
    rows: Record<string, unknown>[],
    previous: Record<string, unknown>[] | undefined,
    dimension: string,
    total: number,
    empty: string,
  ) => {
    const before = indexBy(previous, dimension)
    return (
      <BarRows
        ariaLabel={`Выручка: ${dimension}`}
        series={withMargin ? SALES_SERIES : SALES_SERIES.slice(0, 1)}
        rows={rows.map((row) => {
          const key = rowKey(row, dimension)
          return {
            key: key || '∅',
            label: labelOr(row[dimension], empty),
            sub: [withMargin ? `маржа ${formatPercent(row.margin_percent)}` : null, total > 0 ? `${formatPercent((num(row.revenue) / total) * 100)} выручки` : null]
              .filter(Boolean)
              .join(' · '),
            values: withMargin ? [num(row.revenue), num(row.gross_profit)] : [num(row.revenue)],
            aside: incomplete ? null : <Delta value={deltaPercent(row.revenue, before.get(key)?.revenue)} />,
          }
        })}
      />
    )
  }

  const single = (rows: Record<string, unknown>[], dimension: string, noun: string) =>
    rows.length === 1 ? (
      <div className="single-value">
        <span className="strong">{labelOr(rows[0]![dimension], 'не указано')}</span>
        <span className="small muted">
          — вся выручка периода, {money(num(rows[0]!.revenue))}. Сравнивать {noun} не с чем: в выдаче одно значение.
        </span>
      </div>
    ) : null

  if (!source) {
    return (
      <Panel title="Каналы и юрлица" states={[{ status: 'forbidden', data: null, refreshing: false, message: 'Нужен отчёт «Маржинальность по филиалам» или «Выручка по направлениям».' }]}>
        {() => null}
      </Panel>
    )
  }

  return (
    <div className="fin-stack insights">
      {ctx.partial && <Caveat tone="warn">{ctx.partial}</Caveat>}
      {!withMargin && <Caveat tone="warn">Маржа не входит в ваши права — показана только выручка без НДС.</Caveat>}

      <div className="fin-grid fin-grid--main">
        <Panel
          index={0}
          title="Каналы продаж"
          subtitle={`Без НДС · ${scopeLabel}${incomplete ? '' : ` · справа — выручка ${vs}`}`}
          states={[channels]}
          skeleton={<SkeletonRows rows={4} />}
          empty={salesEmpty(ctx, channelRows.length === 0)}
        >
          {() => single(channelRows, 'channel', 'каналы') ?? breakdown(channelRows, channelsBefore.data?.rows, 'channel', channelTotal, 'Канал не указан')}
        </Panel>

        <Panel
          index={1}
          title="Свои юрлица среди покупателей"
          subtitle="Продажи организациям группы — внутренний оборот"
          states={[kinds]}
          skeleton={<SkeletonRows rows={2} height={18} />}
          empty={salesEmpty(ctx, kindRows.length === 0)}
        >
          {() => (
            <div className="stack" style={{ gap: 14 }}>
              <div className="ageing-head">
                <span className="strong">{internal > 0 ? `${money(internal)} — продажи своим организациям` : 'Продаж своим организациям нет'}</span>
                <span className="small muted">{kindTotal > 0 ? `${formatPercent((internal / kindTotal) * 100)} выручки` : ''}</span>
              </div>
              <SegmentBar
                ariaLabel="Выручка по типу клиента"
                segments={kindRows.map((row) => ({
                  key: String(row.counterparty_kind),
                  label: String(row.counterparty_kind),
                  value: num(row.revenue),
                  color: String(row.counterparty_kind) === 'Своя организация' ? 'var(--series-2)' : 'var(--age-0)',
                }))}
              />
              <ValueLegend
                total={kindTotal}
                items={kindRows.map((row) => ({
                  key: String(row.counterparty_kind),
                  label: labelOr(row.counterparty_kind, 'Тип не указан'),
                  value: num(row.revenue),
                  color: String(row.counterparty_kind) === 'Своя организация' ? 'var(--series-2)' : 'var(--age-0)',
                }))}
              />
              <div className="tiny muted">
                {internal > 0
                  ? 'В консолидированной выручке группы эти продажи считаются дважды: сначала своей организации, потом внешнему клиенту.'
                  : 'Вся выручка периода — внешним клиентам, задвоения нет.'}
              </div>
            </div>
          )}
        </Panel>
      </div>

      <Panel
        index={2}
        title="Каналы по месяцам"
        subtitle="Выручка без НДС за 12 месяцев, выбранный период подсвечен"
        states={[channelTrend]}
        skeleton={<SkeletonColumns height={200} />}
        empty={salesEmpty(ctx, trendSeries.length === 0, 'Продаж за 12 месяцев нет')}
      >
        {() => (
          <ColumnChart
            ariaLabel="Выручка по каналам и месяцам"
            height={200}
            series={trendSeries}
            summaryTitle={labelOf(selection)}
            points={months.map((month) => ({
              key: month,
              label: shortMonth(month),
              title: monthLabel(month),
              highlight: rangeMonths.has(month),
              missing: missingOf(month, coverage),
              values: byMonth.get(month) ?? seriesKeys.map(() => 0),
            }))}
          />
        )}
      </Panel>

      <Panel
        index={3}
        title="Организации группы"
        subtitle={`Выручка без НДС — сопоставима между организациями с разной ставкой НДС${incomplete ? '' : ` · справа — ${vs}`}`}
        states={[orgs]}
        skeleton={<SkeletonRows rows={4} />}
        empty={salesEmpty(ctx, orgRows.length === 0)}
      >
        {() => single(orgRows, 'organization', 'организации') ?? breakdown(orgRows, orgsBefore.data?.rows, 'organization', orgTotal, 'Организация не указана')}
      </Panel>

      <DataNotes states={[channels, orgs, kinds]} />
    </div>
  )
}
